/* RESPONSIBLE TEAM: team-automation-foundations */
/* eslint-disable @intercom/intercom/no-bare-strings */
import type IntlService from 'ember-intl/services/intl';
// @ts-ignore no type declaration available for ember-copy
import { copy } from 'ember-copy';

export function and(...filters: any) {
  filters = filters.compact();
  if (filters.length === 1) {
    return filters[0];
  }

  return {
    type: 'and',
    filters: filters.flatMap((f: any) => (f.type === 'and' ? f.filters : f)),
  };
}

export function not(filter: any) {
  filter = copy(filter, true);
  if (filter.type === 'category') {
    let values = filter.data.values;
    if (values.length === 1 && typeof values.firstObject === 'boolean') {
      filter.data.values = [!values.firstObject];
    } else {
      filter.type = 'not_in_category';
    }
  } else if (filter.type === 'exists') {
    filter.type = 'not_exists';
  } else {
    throw new Error(`Unsupported filter type: ${filter.type}`);
  }
  return filter;
}

const filters = {
  get chatConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['Desktop'],
      },
    };
  },
  get emailConversations() {
    return {
      type: 'category',
      data: {
        property: 'channel_type',
        attribute: 'conversation.channel_type',
        values: ['Email'],
      },
    };
  },

  get sdrParticipated() {
    return {
      type: 'category',
      data: {
        property: 'fin_sdr.participated',
        attribute: 'conversation.fin_sdr.participated',
        values: [true],
      },
    };
  },

  routingOutcome(outcomeTitle: string) {
    return {
      type: 'category',
      data: {
        property: 'fin_sdr.routing_outcome.title',
        attribute: 'conversation.fin_sdr.routing_outcome_title',
        values: [outcomeTitle],
      },
    };
  },

  get capturedContact() {
    return {
      type: 'category',
      data: {
        property: 'fin_sdr.captured_contact',
        attribute: 'conversation.fin_sdr.captured_contact',
        values: [true],
      },
    };
  },

  get noContact() {
    return and(
      {
        type: 'category',
        data: {
          property: 'fin_sdr.captured_contact',
          attribute: 'conversation.fin_sdr.captured_contact',
          values: [false],
        },
      },
      {
        type: 'category',
        data: {
          property: 'fin_sdr.had_prior_contact',
          attribute: 'conversation.fin_sdr.had_prior_contact',
          values: [false],
        },
      },
    );
  },

  get disengaged() {
    return {
      type: 'not_exists',
      data: {
        property: 'fin_sdr.routing_outcome.identifier',
        attribute: 'conversation.fin_sdr.routing_outcome_title',
      },
    };
  },

  get routingOutcomeExists() {
    return {
      type: 'exists',
      data: {
        property: 'fin_sdr.routing_outcome.identifier',
        attribute: 'conversation.fin_sdr.routing_outcome_title',
      },
    };
  },

  get recoveryEmailSent() {
    return {
      type: 'category',
      data: {
        property: 'fin_sdr.recovery_email_sent',
        attribute: 'conversation.fin_sdr.recovery_email_sent',
        values: [true],
      },
    };
  },

  get qualified() {
    return and(this.routingOutcomeExists, {
      type: 'not_in_category',
      data: {
        property: 'fin_sdr.recovery_email_sent',
        attribute: 'conversation.fin_sdr.recovery_email_sent',
        values: [true],
      },
    });
  },

  get disengagedRecovered() {
    return and(this.recoveryEmailSent, this.routingOutcomeExists);
  },

  get finParticipated() {
    return {
      type: 'category',
      data: {
        property: 'fin.participated',
        attribute: 'conversation.fin.participated',
        values: [true],
      },
    };
  },
  get finNotParticipated() {
    return not(this.finParticipated);
  },
};

// Stable internal lookup keys for status-column series — decoupled from translated display
// labels so a routing outcome whose title happens to match a status label (e.g. "Qualified")
// cannot shadow the status series in findBy('label', ...) lookups.
export const STATUS_SERIES_LOOKUP_KEYS: Record<string, string> = {
  Qualified: '__status_Qualified__',
  DisengagedRecovered: '__status_DisengagedRecovered__',
  Disengaged: '__status_Disengaged__',
};

// Static translation key mapping to avoid dynamic translation keys
const METRIC_TRANSLATION_KEYS: Record<string, string> = {
  conversations: 'ai-agent.analyze.sales-performance.funnel-metrics.conversations',
  Chat: 'ai-agent.analyze.sales-performance.funnel-metrics.Chat',
  Email: 'ai-agent.analyze.sales-performance.funnel-metrics.Email',
  Qualified: 'ai-agent.analyze.sales-performance.funnel-metrics.Qualified',
  DisengagedRecovered: 'ai-agent.analyze.sales-performance.funnel-metrics.DisengagedRecovered',
  CapturedContact: 'ai-agent.analyze.sales-performance.funnel-metrics.CapturedContact',
  NoContact: 'ai-agent.analyze.sales-performance.funnel-metrics.NoContact',
  Disengaged: 'ai-agent.analyze.sales-performance.funnel-metrics.Disengaged',
  HandledByServiceAgent: 'ai-agent.analyze.sales-performance.funnel-metrics.HandledByServiceAgent',
  HandledByTeammates: 'ai-agent.analyze.sales-performance.funnel-metrics.HandledByTeammates',
};

export function wrap(filters: any) {
  if (filters && filters.type !== 'and' && filters.type !== 'or') {
    return {
      type: 'and',
      filters: [filters],
    };
  }
  return filters;
}

export function funnelSeries(filters?: any, label?: string) {
  return {
    label,
    metric_id: 'v1.new_conversations',
    filters: wrap(filters),
  };
}

function config(name: string, filters?: any) {
  return { name, series: funnelSeries(filters, name) };
}

export const SdrQualificationFunnelConfig = {
  get filters() {
    return filters;
  },

  buildChartSeriesFilters(routingOutcomes: Array<{ identifier: string; title: string }>) {
    let escalateOutcome = routingOutcomes.find(
      (o) => o.identifier.toLowerCase() === 'escalate_to_support',
    );
    let disqualifiedOutcome = routingOutcomes.find(
      (o) => o.identifier.toLowerCase() === 'disqualified',
    );

    // Exclude status-node outcomes from Qualified/DisengagedRecovered to prevent double-counting.
    // escalate_to_support and disqualified are status nodes counted via direct channel links.
    let notEscalated = escalateOutcome
      ? not(this.filters.routingOutcome(escalateOutcome.title))
      : null;
    let notDisqualified = disqualifiedOutcome
      ? not(this.filters.routingOutcome(disqualifiedOutcome.title))
      : null;
    let qualifiedFilter = and(this.filters.qualified, notEscalated, notDisqualified);
    let disengagedRecoveredFilter = and(
      this.filters.disengagedRecovered,
      notEscalated,
      notDisqualified,
    );

    let seriesFilters = [
      config('conversations', this.filters.sdrParticipated),
      config('Chat', and(this.filters.sdrParticipated, this.filters.chatConversations)),
      config('Email', and(this.filters.sdrParticipated, this.filters.emailConversations)),
      config('Qualified', and(this.filters.sdrParticipated, qualifiedFilter)),
      config('DisengagedRecovered', and(this.filters.sdrParticipated, disengagedRecoveredFilter)),
    ];

    routingOutcomes.forEach((outcome) => {
      seriesFilters.push(
        config(
          outcome.identifier,
          and(this.filters.sdrParticipated, this.filters.routingOutcome(outcome.title)),
        ),
      );
    });

    routingOutcomes.forEach((outcome) => {
      let id = outcome.identifier.toLowerCase();
      if (id === 'escalate_to_support' || id === 'disqualified') {
        return; // status nodes — linked directly from channels, not via Qualified/DisengagedRecovered
      }
      seriesFilters.push(
        config(
          `QualifiedAnd${outcome.identifier}`,
          and(
            this.filters.sdrParticipated,
            qualifiedFilter,
            this.filters.routingOutcome(outcome.title),
          ),
        ),
        config(
          `DisengagedRecoveredAnd${outcome.identifier}`,
          and(
            this.filters.sdrParticipated,
            disengagedRecoveredFilter,
            this.filters.routingOutcome(outcome.title),
          ),
        ),
      );
    });

    if (escalateOutcome) {
      seriesFilters.push(
        config(
          `ChatAnd${escalateOutcome.identifier}`,
          and(
            this.filters.sdrParticipated,
            this.filters.chatConversations,
            this.filters.routingOutcome(escalateOutcome.title),
          ),
        ),
        config(
          `EmailAnd${escalateOutcome.identifier}`,
          and(
            this.filters.sdrParticipated,
            this.filters.emailConversations,
            this.filters.routingOutcome(escalateOutcome.title),
          ),
        ),
        config(
          'escalate_to_supportAndHandledByServiceAgent',
          and(
            this.filters.sdrParticipated,
            this.filters.routingOutcome(escalateOutcome.title),
            this.filters.finParticipated,
          ),
        ),
        config(
          'escalate_to_supportAndHandledByTeammates',
          and(
            this.filters.sdrParticipated,
            this.filters.routingOutcome(escalateOutcome.title),
            this.filters.finNotParticipated,
          ),
        ),
      );
    }

    if (disqualifiedOutcome) {
      seriesFilters.push(
        config(
          `ChatAnd${disqualifiedOutcome.identifier}`,
          and(
            this.filters.sdrParticipated,
            this.filters.chatConversations,
            this.filters.routingOutcome(disqualifiedOutcome.title),
          ),
        ),
        config(
          `EmailAnd${disqualifiedOutcome.identifier}`,
          and(
            this.filters.sdrParticipated,
            this.filters.emailConversations,
            this.filters.routingOutcome(disqualifiedOutcome.title),
          ),
        ),
      );
    }

    seriesFilters.push(
      config('CapturedContact', and(this.filters.sdrParticipated, this.filters.capturedContact)),
      config('NoContact', and(this.filters.sdrParticipated, this.filters.noContact)),
      config('Disengaged', and(this.filters.sdrParticipated, this.filters.disengaged)),
    );

    seriesFilters.push(
      config(
        'ChatAndQualified',
        and(this.filters.sdrParticipated, this.filters.chatConversations, qualifiedFilter),
      ),
      config(
        'EmailAndQualified',
        and(this.filters.sdrParticipated, this.filters.emailConversations, qualifiedFilter),
      ),
      config(
        'ChatAndDisengagedRecovered',
        and(
          this.filters.sdrParticipated,
          this.filters.chatConversations,
          disengagedRecoveredFilter,
        ),
      ),
      config(
        'EmailAndDisengagedRecovered',
        and(
          this.filters.sdrParticipated,
          this.filters.emailConversations,
          disengagedRecoveredFilter,
        ),
      ),
      config(
        'ChatAndDisengaged',
        and(this.filters.sdrParticipated, this.filters.chatConversations, this.filters.disengaged),
      ),
      config(
        'EmailAndDisengaged',
        and(this.filters.sdrParticipated, this.filters.emailConversations, this.filters.disengaged),
      ),
    );

    seriesFilters.push(
      config(
        'DisengagedAndCapturedContact',
        and(this.filters.sdrParticipated, this.filters.disengaged, this.filters.capturedContact),
      ),
      config(
        'DisengagedAndNoContact',
        and(this.filters.sdrParticipated, this.filters.disengaged, this.filters.noContact),
      ),
    );

    return seriesFilters;
  },

  getChartSeriesWithLabels(
    intl: IntlService,
    routingOutcomes: Array<{ identifier: string; title: string }>,
  ) {
    let seriesFilters = this.buildChartSeriesFilters(routingOutcomes);
    let outcomeMap = new Map(routingOutcomes.map((o) => [o.identifier, o.title]));

    return seriesFilters.map((config) => {
      let label: string;

      if (outcomeMap.has(config.name)) {
        label = outcomeMap.get(config.name)!;
      } else if (config.name.includes('And')) {
        let parts = config.name.split('And');
        let labelParts = parts.map((part) => {
          if (outcomeMap.has(part)) {
            return outcomeMap.get(part)!;
          }
          let translationKey = METRIC_TRANSLATION_KEYS[part];
          return translationKey ? intl.t(translationKey) : part;
        });
        label = labelParts.join(' & ');
      } else if (STATUS_SERIES_LOOKUP_KEYS[config.name] !== undefined) {
        label = STATUS_SERIES_LOOKUP_KEYS[config.name]!;
      } else {
        let translationKey = METRIC_TRANSLATION_KEYS[config.name];
        label = translationKey ? intl.t(translationKey) : config.name;
      }

      return {
        ...config.series,
        label,
      };
    });
  },

  getMetricsFromChartDataRequest(
    chartData: any,
    routingOutcomes: Array<{ identifier: string; title: string }>,
  ) {
    let seriesFilters = this.buildChartSeriesFilters(routingOutcomes);
    let result: any = {};
    for (let i = 0; i < chartData.length; i++) {
      let seriesName = seriesFilters[i].name;
      let seriesData = chartData[i].data[0];
      result[seriesName] = seriesData;
    }
    return result;
  },
};
